import { useEffect, useState } from 'react';
import { getAssetDownloadUrl } from '../../services/assetUrlUtils';
import { getUserSasToken } from '../../services/characterStorageService';

/**
 * URLs that never need SAS signing: root-relative/same-origin asset paths
 * (served by the local build) and absolute non-blob references (CDN links,
 * blob:, data:). Mirrors the classification in
 * `assetUrlUtils.getAssetDownloadUrl`, which also passes these through
 * unchanged.
 */
const isDirectSupportUrl = (value?: string | null): value is string => {
  if (!value) return false;
  if (/^[/.]+/.test(value)) return true;
  if (!/^(https?:|blob:|data:)/i.test(value)) return false;
  return !/^https?:\/\/[^/]*blob\.core\.windows\.net\//i.test(value);
};

/**
 * Resolve a stored support asset location into a playable URL.
 *
 * Blob-relative paths (e.g. `support/videos/intro.mp4`) are prefixed with the
 * storage base URL and signed with the stored user-level SAS token (cached in
 * app storage, transparently refreshed near expiry). Direct references pass
 * through unchanged.
 */
export const resolveSupportAssetUrl = async (
  location?: string | null
): Promise<string> => {
  if (!location) return '';
  if (isDirectSupportUrl(location)) return location;

  const sasToken = await getUserSasToken();
  return getAssetDownloadUrl(location, sasToken);
};

/**
 * React hook version of {@link resolveSupportAssetUrl} for rendering
 * (`<img src>` / `<video src>`). Direct URLs resolve synchronously;
 * blob-relative paths resolve asynchronously once the user SAS is available.
 */
export const useSupportAssetUrl = (location?: string | null): string => {
  const [url, setUrl] = useState<string>(() =>
    isDirectSupportUrl(location) ? location : ''
  );

  useEffect(() => {
    let cancelled = false;

    if (isDirectSupportUrl(location)) {
      setUrl(location);
      return () => {
        cancelled = true;
      };
    }

    setUrl('');
    resolveSupportAssetUrl(location)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch(() => {
        if (!cancelled) setUrl(location || '');
      });

    return () => {
      cancelled = true;
    };
  }, [location]);

  return url;
};
